import { useState, useCallback } from "react";
import { addUniqueItems } from "../utils/assignmentUtils";
import { useLatestRequest } from "./useLatestRequest";

/** What a search fetcher must return; null means the request failed. */
export interface SearchPage<T> {
  items: T[];
  total: number;
  page: number;
  lastPage: number;
}

/**
 * `extra` carries a per-search filter alongside the term - the program
 * search passes a challenge id. It is remembered, so loadMore and
 * changeSearchPage stay within the same filtered result set.
 */
export type SearchFetcher<T, E = void> = (
  searchTerm: string,
  page: number,
  limit: number,
  extra?: E
) => Promise<SearchPage<T> | null>;

/**
 * Search with "load more" paging, shared by the user, program, exercise and
 * recipe search panels.
 *
 * Those four were near-identical ~130-line copies: the same nine state
 * slots, byte-identical clearSearch and updateTotal, and the same
 * dedupe-on-append written out four times with only the loop variable
 * changed - despite utils/assignmentUtils.addUniqueItems already being
 * exactly that function.
 *
 * The one apparent behavioural difference between them was in useExercises,
 * which computed hasMore as
 *   (total > 60 || total > loaded) && current_page < last_page
 * That reduces to `current_page < last_page`: when there is a further page,
 * total necessarily exceeds what has been loaded, so the left side is always
 * true. All four are therefore unified on the simple rule.
 */
export const useEntitySearch = <T extends { id: number }, E = void>(
  fetcher: SearchFetcher<T, E>,
  { limit, errorMessage }: { limit: number; errorMessage: string }
) => {
  const [results, setResults] = useState<T[]>([]);
  const [loading, setLoading] = useState(false);
  const [loadingMore, setLoadingMore] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [hasSearched, setHasSearched] = useState(false);
  const [total, setTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [hasMore, setHasMore] = useState(false);
  const [lastSearchTerm, setLastSearchTerm] = useState("");
  const [lastExtra, setLastExtra] = useState<E | undefined>(undefined);
  const startRequest = useLatestRequest();

  const search = useCallback(
    async (
      searchTerm: string,
      pageNum: number = 1,
      append: boolean = false,
      extra?: E
    ) => {
      const isCurrent = startRequest();

      if (!searchTerm.trim()) {
        setResults([]);
        setTotal(0);
        setHasSearched(false);
        setHasMore(false);
        setLoading(false);
        setLoadingMore(false);
        return;
      }

      if (append) {
        setLoadingMore(true);
      } else {
        setLoading(true);
        setHasSearched(true);
      }
      setError(null);
      setLastSearchTerm(searchTerm);
      setLastExtra(extra);
      setPage(pageNum);

      try {
        const response = await fetcher(searchTerm, pageNum, limit, extra);
        if (!isCurrent()) return;

        if (response) {
          setResults((prev) =>
            append
              ? addUniqueItems(prev, response.items, (item) => item.id)
              : response.items
          );
          setTotal(response.total);
          setHasMore(response.page < response.lastPage);
        } else {
          setError(errorMessage);
          // On "load more" the already-loaded results stay put; a failed
          // extra page should not blank the list the user is reading.
          if (!append) {
            setResults([]);
            setTotal(0);
          }
          setHasMore(false);
        }
      } catch (err) {
        if (!isCurrent()) return;
        setError(errorMessage);
        if (!append) {
          setResults([]);
          setTotal(0);
        }
        setHasMore(false);
      } finally {
        if (isCurrent()) {
          setLoading(false);
          setLoadingMore(false);
        }
      }
    },
    [fetcher, limit, errorMessage, startRequest]
  );

  const loadMore = useCallback(() => {
    if (lastSearchTerm && hasMore && !loading && !loadingMore) {
      search(lastSearchTerm, page + 1, true, lastExtra);
    }
  }, [lastSearchTerm, lastExtra, hasMore, loading, loadingMore, page, search]);

  const clearSearch = useCallback(() => {
    startRequest();
    setResults([]);
    setError(null);
    setLoading(false);
    setLoadingMore(false);
    setHasSearched(false);
    setTotal(0);
    setPage(1);
    setHasMore(false);
    setLastSearchTerm("");
    setLastExtra(undefined);
  }, [startRequest]);

  const changeSearchPage = useCallback(
    (newPage: number) => {
      if (lastSearchTerm) search(lastSearchTerm, newPage, false, lastExtra);
    },
    [lastSearchTerm, lastExtra, search]
  );

  // Lets a screen correct the count after removing a row, without refetching.
  const updateTotal = useCallback((newTotal: number) => {
    setTotal(Math.max(0, newTotal));
  }, []);

  return {
    results,
    loading,
    loadingMore,
    error,
    hasSearched,
    total,
    page,
    hasMore,
    totalPages: Math.ceil(total / limit),
    lastSearchTerm,
    search,
    loadMore,
    clearSearch,
    changeSearchPage,
    updateTotal,
  };
};
